/**
 * UI Store
 *
 * Manages global UI state including:
 * - Sidebar collapsed state
 * - Dark mode (future)
 * - Notification preferences
 * - Global loading states
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

const SIDEBAR_KEY = 'claimshield_sidebar_collapsed'
const THEME_KEY = 'claimshield_theme'

export type Theme = 'light' | 'dark' | 'system'

export interface Notification {
  id: string
  type: 'success' | 'info' | 'warning' | 'error'
  title: string
  message?: string
  duration?: number
}

export const useUIStore = defineStore('ui', () => {
  // ===== State =====

  // Sidebar
  const sidebarCollapsed = ref(false)
  const sidebarMobileOpen = ref(false)

  // Theme
  const theme = ref<Theme>('light')

  // Global loading overlay
  const globalLoading = ref(false)
  const globalLoadingMessage = ref('')

  // Command palette (future)
  const commandPaletteOpen = ref(false)

  // ===== Getters =====

  const isDarkMode = computed(() => {
    if (theme.value === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return theme.value === 'dark'
  })

  // ===== Actions =====

  /**
   * Initialize UI state from storage
   */
  function initialize(): void {
    // Restore sidebar state
    const storedSidebar = localStorage.getItem(SIDEBAR_KEY)
    if (storedSidebar !== null) {
      sidebarCollapsed.value = storedSidebar === 'true'
    }

    // Restore theme
    const storedTheme = localStorage.getItem(THEME_KEY) as Theme | null
    if (storedTheme && ['light', 'dark', 'system'].includes(storedTheme)) {
      theme.value = storedTheme
    }

    // Apply theme to document
    applyTheme()
  }

  /**
   * Toggle sidebar collapsed state
   */
  function toggleSidebar(): void {
    sidebarCollapsed.value = !sidebarCollapsed.value
    localStorage.setItem(SIDEBAR_KEY, String(sidebarCollapsed.value))
  }

  /**
   * Toggle mobile sidebar
   */
  function toggleMobileSidebar(): void {
    sidebarMobileOpen.value = !sidebarMobileOpen.value
  }

  /**
   * Close mobile sidebar
   */
  function closeMobileSidebar(): void {
    sidebarMobileOpen.value = false
  }

  /**
   * Set theme
   */
  function setTheme(newTheme: Theme): void {
    theme.value = newTheme
    localStorage.setItem(THEME_KEY, newTheme)
    applyTheme()
  }

  /**
   * Apply theme to document
   */
  function applyTheme(): void {
    const root = document.documentElement

    if (isDarkMode.value) {
      root.classList.add('dark-mode')
    } else {
      root.classList.remove('dark-mode')
    }
  }

  /**
   * Show global loading overlay
   */
  function showLoading(message = 'Loading...'): void {
    globalLoading.value = true
    globalLoadingMessage.value = message
  }

  /**
   * Hide global loading overlay
   */
  function hideLoading(): void {
    globalLoading.value = false
    globalLoadingMessage.value = ''
  }

  /**
   * Toggle command palette
   */
  function toggleCommandPalette(): void {
    commandPaletteOpen.value = !commandPaletteOpen.value
  }

  // Watch for system theme changes
  if (typeof window !== 'undefined') {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (theme.value === 'system') {
        applyTheme()
      }
    })
  }

  return {
    // State
    sidebarCollapsed,
    sidebarMobileOpen,
    theme,
    globalLoading,
    globalLoadingMessage,
    commandPaletteOpen,

    // Getters
    isDarkMode,

    // Actions
    initialize,
    toggleSidebar,
    toggleMobileSidebar,
    closeMobileSidebar,
    setTheme,
    showLoading,
    hideLoading,
    toggleCommandPalette,
  }
})
